import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { History, Trash2, CircleHelp } from "lucide-react";
import { getAuthToken } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";
import { Review } from "@/components/Review";

interface PracticeHistoryItem {
  _id: string;
  sessionType: string;
  language: string;
  totalQuestions: number;
  score: number;
  percentage: number;
  timeTaken: number;
  createdAt: string;
  answers: {
    questionId: string;
    selectedOption: number;
    correctAnswer: number;
  }[];
}

const apiURL = import.meta.env.VITE_API_URL || "http://localhost:7845";

export function PracticeHistory() {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedTimeframe, setSelectedTimeframe] = useState("all");
  const [reviewSessionId, setReviewSessionId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch history
  const { data: history, isLoading } = useQuery<PracticeHistoryItem[]>({
    queryKey: ["/api/history", selectedCategory, selectedTimeframe],
    queryFn: async () => {
      const token = getAuthToken();
      const params = new URLSearchParams();
      if (selectedCategory !== "all") params.append("category", selectedCategory);
      if (selectedTimeframe !== "all") params.append("time", selectedTimeframe);

      const url = `${apiURL}/api/examination/prachist`;
      // const url = `${apiURL}/api/examination${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to fetch history");
      return response.json();
    },
  });

  // Clear history
  const clearHistoryMutation = useMutation({
    mutationFn: async () => {
      const token = getAuthToken();
      const response = await fetch(`${apiURL}/api/examination/${1}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to clear history");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/history"] });
      toast({
        title: "History Cleared",
        description: "Your practice history has been cleared successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to clear history. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleClearHistory = () => {
    if (
      window.confirm(
        "Are you sure you want to clear your practice history? This action cannot be undone."
      )
    ) {
      clearHistoryMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <Skeleton className="h-8 w-64 mx-auto mb-4" />
        <div className="space-y-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="p-6">
              <Skeleton className="h-6 w-64 mb-4" />
              {Array.from({ length: 4 }).map((_, j) => (
                <Skeleton key={j} className="h-12 w-full mb-2" />
              ))}
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 flex items-center justify-center space-x-3">
          <History className="text-[hsl(201,91%,60%)]" size={32} />
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
            Practice History
          </h2>
        </div>

        {/* Filters */}
        {/* <Card className="p-6 mb-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-4">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="gk">GK</SelectItem>
                  <SelectItem value="math">Math</SelectItem>
                  <SelectItem value="reasoning">Reasoning</SelectItem>
                  <SelectItem value="english">English</SelectItem>
                </SelectContent>
              </Select>

              <Select value={selectedTimeframe} onValueChange={setSelectedTimeframe}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Timeframe" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">Last Week</SelectItem>
                  <SelectItem value="month">Last Month</SelectItem>
                  <SelectItem value="all">All Time</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleClearHistory}
              variant="destructive"
              disabled={clearHistoryMutation.isPending}
            >
              <Trash2 size={16} className="mr-2" /> Clear History
            </Button>
          </div>
        </Card> */}

        {/* History list */}
        <div className="space-y-6">
          {!history || history.length === 0 ? (
            <Card className="p-12 text-center">
              <CircleHelp size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                No Practice History
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Start practicing to see your progress and performance history here!
              </p>
            </Card>
          ) : (
            history.map((session) => (
              <Card key={session._id} className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {session.sessionType.toUpperCase()} Practice Session
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {new Date(session.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-[hsl(142,76%,36%)]">
                      {session.percentage}%
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {session.score}/{session.totalQuestions} correct
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-4">
                  <Button
                    onClick={() => setReviewSessionId(session._id)}
                    variant="outline"
                  >
                    Review
                  </Button>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Review modal */}
      {reviewSessionId && (
        <Review
          sessionId={reviewSessionId}
          onClose={() => setReviewSessionId(null)}
        />
      )}
    </div>
  );
}
